import { Spinner, SpinnerSize } from "@fluentui/react";
import { DocumentSearchIcon } from "@fluentui/react-icons-mdl2";
import { useEffect, useState } from "react";

import { Tariff } from "../../interfaces/invoices-response";

import {
  Container,
  ContentWithPdf,
  PdfContainer,
  PdfFrame,
  EmptyState,
  EmptyContent,
  EmptyIcon,
  EmptyText
} from "./style";

import { useGetInvoicePdf } from "@/core/libs/api/react-query/hook/use-get-invoice-pdf";
import { useGetTariffPdf } from "@/core/libs/api/react-query/hook/use-get-tariff-pdf";

interface MainContentProps {
  selectedTariff?: Tariff;
  isLoading?: boolean;
  invoiceId?: number;
  document?: string;
}

export default function MainContent({
  selectedTariff,
  isLoading = false,
  invoiceId,
  document
}: MainContentProps) {
  const [pdfUrl, setPdfUrl] = useState<string>("");
  const [pdfLoading, setPdfLoading] = useState<boolean>(false);
  const [hasPdfError, setHasPdfError] = useState<boolean>(false);
  const [isViewingInvoicePdf, setIsViewingInvoicePdf] =
    useState<boolean>(false);

  const { getTariffPdf } = useGetTariffPdf({
    invoiceId: invoiceId,
    tariffId: selectedTariff?.id,
    document: document || ""
  });

  const { getInvoicePdf } = useGetInvoicePdf({
    invoiceId,
    document: document || ""
  });

  useEffect(() => {
    let pdfObjectUrl: string | null = null;

    if (!invoiceId || !document) {
      setPdfUrl("");
      return;
    }

    setPdfLoading(true);
    setHasPdfError(false);

    if (!selectedTariff && invoiceId && document) {
      setIsViewingInvoicePdf(true);
      setPdfUrl("");

      getInvoicePdf()
        .then(response => {
          const blob = new Blob([response.data], { type: "application/pdf" });
          pdfObjectUrl = URL.createObjectURL(blob);
          setPdfUrl(pdfObjectUrl);
        })
        .catch(error => {
          setHasPdfError(true);
          throw new Error("Erro ao buscar PDF da fatura:", error);
        })
        .finally(() => {
          setPdfLoading(false);
        });
    } else if (selectedTariff && invoiceId && document) {
      setIsViewingInvoicePdf(false);
      setPdfUrl("");

      getTariffPdf()
        .then(response => {
          const blob = new Blob([response.data], { type: "application/pdf" });
          pdfObjectUrl = URL.createObjectURL(blob);
          setPdfUrl(pdfObjectUrl);
        })
        .catch(error => {
          setHasPdfError(true);
          throw new Error(`Erro ao buscar PDF: ${error}`);
        })
        .finally(() => {
          setPdfLoading(false);
        });
    }

    return () => {
      if (pdfObjectUrl) {
        URL.revokeObjectURL(pdfObjectUrl);
      }
    };
  }, [selectedTariff, invoiceId, document, getTariffPdf, getInvoicePdf]);

  return (
    <Container>
      {(() => {
        if (isLoading || pdfLoading) {
          return (
            <EmptyState>
              <EmptyContent>
                <Spinner size={SpinnerSize.large} label="Carregando..." />
              </EmptyContent>
            </EmptyState>
          );
        }

        if (pdfUrl && !hasPdfError) {
          if (selectedTariff) {
            return (
              <ContentWithPdf>
                <PdfContainer>
                  <PdfFrame
                    src={pdfUrl}
                    title={`Tarifa - ${selectedTariff.identifier}`}
                  />
                </PdfContainer>
              </ContentWithPdf>
            );
          }

          if (isViewingInvoicePdf) {
            return (
              <ContentWithPdf>
                <PdfContainer>
                  <PdfFrame src={pdfUrl} title="PDF - Fatura Resumida" />
                </PdfContainer>
              </ContentWithPdf>
            );
          }
        }

        return (
          <EmptyState>
            <EmptyContent>
              <EmptyIcon>
                <DocumentSearchIcon />
              </EmptyIcon>
              <EmptyText>
                Selecione a fatura e a tarifa ao lado para visualizar o PDF do
                documento.
              </EmptyText>
            </EmptyContent>
          </EmptyState>
        );
      })()}
    </Container>
  );
}
